// @ts-nocheck
// CAMINHO: src/pages/Cases.tsx
// VERSÃO: SIMPLIFICADA PARA DEBUG

import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Plus, Scale, Archive, BookOpen, AlertTriangle, Clock, DollarSign, TrendingUp, Sparkles, Building2, LayoutGrid, List, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { createFinancialEntriesFromContract, saveFinancialEntries } from "@/utils/createFinancialEntries";
import { useLocation, useNavigate } from "react-router-dom";
import { datajudApi } from "@/services/datajudAPI";
import type { IntimacaoPrefill } from "@/components/intimacoes/RegisterFromIntimacaoDialog";
import { ClientQuickEditDialog } from "@/components/clients/ClientQuickEditDialog";
import { EmailCaseSyncButton } from "@/components/cases/EmailCaseSyncButton";
import { requestPortalSync } from "@/services/googleCalendarSync";

import { Case } from "@/types/case";
import { CaseAnalytics, getCaseKpis } from "@/components/CaseAnalytics";
import { CasesFilters } from "@/components/cases/CasesFilters";
import { CaseListItem } from "@/components/cases/CaseListItem";
import { CaseDetailModal } from "@/components/cases/CaseDetailModal";
import { EventFormModal } from "@/components/EventFormModal";
import { DatajudSearchModal } from "@/components/DatajudSearchModal";
import { ImportOabProcessesModal } from "@/components/cases/ImportOabProcessesModal";
import { DatajudDiagnostic } from "@/components/DatajudDiagnostic";
import { EnhancedCaseForm } from "@/components/cases/EnhancedCaseForm";
import { FinancialEntriesService } from "@/services/financialEntriesService";
import AdministrativeProcessModal from "@/components/cases/AdministrativeProcessModal";
import { Badge } from "@/components/ui/badge";
import { numberToBRLInputFormat, parseBRLStringToNumber } from "@/utils/brlFormat";

interface Client {
  id: string;
  name: string;
  email: string;
}

const Cases = () => {
  const [cases, setCases] = useState<Case[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [courtLevelFilter, setCourtLevelFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [kpiKey, setKpiKey] = useState<string | null>(null);
  const [processTypeFilter, setProcessTypeFilter] = useState<"all" | "judicial" | "administrative">("all");
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCaseForEvent, setSelectedCaseForEvent] = useState<Case | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [syncingCases, setSyncingCases] = useState<Set<string>>(new Set());
  const [consorcioClientsCase, setConsorcioClientsCase] = useState<Case | null>(null);
  
  // Estados para modais de estatísticas
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsModalType, setStatsModalType] = useState<string>('');
  const [statsModalTitle, setStatsModalTitle] = useState<string>('');
  const [statsModalCases, setStatsModalCases] = useState<Case[]>([]);

  // Estados para modal de processo administrativo
  const [isAdministrativeModalOpen, setIsAdministrativeModalOpen] = useState(false);

  const { toast } = useToast();
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  const [formData, setFormData] = useState({ 
    case_number: "", 
    case_number_cnj: "", 
    internal_number: "", 
    title: "", 
    description: "", 
    case_type: "", 
    area_of_law: "", 
    subject_matter: "", 
    case_value: "", 
    risk_classification: "medio" as 'baixo' | 'medio' | 'alto' | 'critico', 
    court: "", 
    court_level: "1_instancia" as '1_instancia' | '2_instancia' | 'superior' | 'supremo', 
    jurisdiction: "", 
    vara: "", 
    forum: "", 
    filing_date: undefined as Date | undefined, 
    deadline: undefined as Date | undefined, 
    next_hearing: undefined as Date | undefined, 
    statute_limitations: undefined as Date | undefined, 
    status: "Em andamento", 
    phase: "", 
    priority: "Média", 
    confidentiality_level: "normal" as 'normal' | 'restrito' | 'secreto', 
    lawyer_responsible: "", 
    client_id: "", 
    is_litisconsorcio: false, 
    selected_clients: [] as Array<{ id: string; name: string; email: string; is_primary: boolean }>,
    opposing_party: "", 
    opposing_counsel: "", 
    client_fee: "", 
    court_costs: "", 
    estimated_duration: "", 
    success_fee: "", 
    strategy_notes: "", 
    internal_notes: "", 
    client_objectives: "", 
    monitoring_deadlines: true, 
    critical_deadlines: [] as string[], 
    alert_days_before: 5, 
    tags: [] as string[], 
    archived: false, 
    archive_reason: "",
    // Novos campos financeiros
    financial_responsible: "",
    payment_method: "a_vista" as 'a_vista' | 'parcelado' | 'condicional' | 'por_fase',
    installment_count: 1,
    installment_frequency: "mensal" as 'mensal' | 'bimestral' | 'trimestral' | 'semestral' | 'anual',
    first_payment_date: undefined as Date | undefined,
    late_payment_interest: "",
    early_payment_discount: "",
    payment_conditions: "",
    collection_notes: "",
    phase_fees: [] as Array<{
      phase: string;
      amount: string;
      due_date?: Date;
      description: string;
    }>,
    success_fee_type: "percentage" as 'percentage' | 'fixed' | 'conditional',
    success_fee_conditions: "",
    success_fee_trigger: "sentenca" as 'sentenca' | 'acordo' | 'liminar' | 'outro'
  });

  const navigate = useNavigate();
  const location = useLocation();
  // Cadastro vindo de uma intimação: depois de salvar, abre a página do processo
  const fromIntimacaoRef = useRef(false);
  // Cliente aberto para completar o cadastro sem sair do formulário do processo
  const [clientToComplete, setClientToComplete] = useState<string | null>(null);
  // ?atualizados=1 (aviso de e-mail): mostra só os processos com movimentação nova
  const [onlyUpdated, setOnlyUpdated] = useState(() => new URLSearchParams(window.location.search).get('atualizados') === '1');

  const formatCurrencyValue = (value?: number | string) => {
    if (value === undefined || value === null || value === "") return '-';
    const numberValue = typeof value === 'string' ? parseFloat(value.toString().replace(/[\D]/g, '').replace(',', '.')) : value;
    if (isNaN(numberValue)) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numberValue as number);
  };

  const formatProcessNumber = (case_: Case) => {
    if (case_.process_type === 'administrative') {
      return case_.process_number || '-';
    }
    const number = case_.case_number_cnj || '';
    const clean = number.replace(/\D/g, '');
    if (clean.length >= 20) {
      const sequencial = clean.substring(0, 7);
      const digito = clean.substring(7, 9);
      const ano = clean.substring(9, 13);
      const segmento = clean.substring(13, 14);
      const tribunal = clean.substring(14, 16);
      const origem = clean.substring(16, 20);
      return `${sequencial}-${digito}.${ano}.${segmento}.${tribunal}.${origem}`;
    }
    return number || '-';
  };

  const formatCaseDate = (date?: string) => {
    if (!date) return '-';
    try {
      return new Intl.DateTimeFormat('pt-BR').format(new Date(date));
    } catch {
      return '-';
    }
  };

  const getCaseClientName = (case_: Case) => {
    if (case_.is_litisconsorcio && case_.relatedClients && Array.isArray(case_.relatedClients)) {
      return case_.relatedClients.map((client: any) => client.name).filter(Boolean).join(', ') || 'Cliente não informado';
    }
    if (case_.client_id) {
      const client = clients.find(c => c.id === case_.client_id);
      return client?.name || 'Cliente não informado';
    }
    return 'Cliente não informado';
  };

  const getDeadlineInfo = (deadline?: string) => {
    if (!deadline) return null;
    const today = new Date();
    const target = new Date(deadline);
    if (isNaN(target.getTime())) return null;
    const diff = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { text: `${Math.abs(diff)} dias em atraso`, color: 'text-red-600' };
    if (diff === 0) return { text: 'Vence hoje', color: 'text-red-600' };
    if (diff <= 7) return { text: `${diff} dias`, color: 'text-orange-600' };
    if (diff <= 30) return { text: `${diff} dias`, color: 'text-yellow-600' };
    return { text: `${diff} dias`, color: 'text-green-600' };
  };

  const getPrimaryClientName = (case_: Case) => {
    if (case_.is_litisconsorcio) {
      const all = case_.relatedClients || case_.clients;
      if (Array.isArray(all) && all.length > 0) {
        return all[0]?.name || 'Cliente não informado';
      }
    }
    return getCaseClientName(case_);
  };

  const getConsorcioClientNames = (case_: Case) => {
    const all = case_.relatedClients || case_.clients;
    if (!Array.isArray(all) || all.length === 0) return [];
    return all.map((client: any) => client?.name).filter(Boolean);
  };

  useEffect(() => {
    // Verificar se há parâmetro de rota para filtrar por tipo de processo
    const hash = window.location.hash;
    if (hash === '#adm') {
      setProcessTypeFilter('administrative');
    } else if (hash === '#jud') {
      setProcessTypeFilter('judicial');
    } else {
      setProcessTypeFilter('all');
    }
    // Inicializar termo de busca a partir de query string (?q=)
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      if (q) setSearchTerm(q);
    } catch (_) {}
    
    fetchCases();
    fetchClients();
  }, []);

  // Fechou o formulário sem salvar: o próximo cadastro volta ao fluxo normal
  useEffect(() => {
    if (!isDialogOpen) fromIntimacaoRef.current = false;
  }, [isDialogOpen]);

  // Intimações > "Cadastrar processo": abre o formulário já preenchido
  useEffect(() => {
    const prefill = (location.state as { intimacaoPrefill?: IntimacaoPrefill } | null)?.intimacaoPrefill;
    if (!prefill) return;
    navigate(location.pathname, { replace: true, state: null });

    (async () => {
      toast({ title: 'Buscando dados do processo...', description: 'Completando com as informações do Datajud (CNJ).' });
      let datajud: any = null;
      try {
        const result: any = await Promise.race([
          datajudApi.searchByProcessNumber(prefill.numeroMascara),
          new Promise((resolve) => setTimeout(() => resolve(null), 12000)),
        ]);
        if (result?.success && result.data?.[0]) datajud = result.data[0];
      } catch {
        datajud = null;
      }

      const parte = prefill.client?.name || prefill.autores[0] || prefill.reus[0];
      const assunto = datajud?.assunto?.[0]?.nome || '';
      const resumo = [
        `Processo cadastrado a partir de intimação de ${prefill.dataDisponibilizacao.split('-').reverse().join('/')} (${prefill.sistema}).`,
        prefill.autores.length ? `Polo ativo: ${prefill.autores.join(', ')}` : '',
        prefill.reus.length ? `Polo passivo: ${prefill.reus.join(', ')}` : '',
        assunto ? `Assunto: ${assunto}` : '',
      ].filter(Boolean).join('\n');

      setFormData((current: any) => ({
        ...current,
        title: parte ? `${prefill.classe} - ${parte}` : prefill.classe,
        case_number_cnj: prefill.numeroMascara,
        case_type: prefill.classe,
        court: prefill.tribunal,
        vara: prefill.orgao,
        jurisdiction: datajud?.orgaoJulgador && datajud.orgaoJulgador !== 'Não informado' ? datajud.orgaoJulgador : current.jurisdiction,
        subject_matter: assunto || current.subject_matter,
        filing_date: datajud?.dataAjuizamento ? new Date(datajud.dataAjuizamento) : current.filing_date,
        opposing_party: prefill.reus.filter((r) => r !== prefill.client?.name).join(', ') || current.opposing_party,
        description: resumo,
        internal_notes: `Última intimação (${prefill.dataDisponibilizacao.split('-').reverse().join('/')}):\n${prefill.texto}${prefill.link ? `\n\nLink: ${prefill.link}` : ''}`,
        status: 'Em andamento',
        priority: 'Média',
        client_id: prefill.client?.id || '',
        selected_clients: prefill.client ? [{ ...prefill.client, is_primary: true }] : [],
        ...(datajud ? { datajud_json: datajud } : {}),
      }));
      setEditingCase(null);
      fromIntimacaoRef.current = true;
      setIsDialogOpen(true);
      toast({
        title: datajud ? 'Dados do processo carregados' : 'Dados da intimação carregados',
        description: datajud
          ? 'Informações da intimação e do Datajud preenchidas. Complete o que faltar e salve.'
          : 'O Datajud não retornou dados agora. Complete o que faltar e salve.',
      });
    })();
  }, [location.state]);

  // Debug para modal de estatísticas
  useEffect(() => {
    console.log('Modal stats estado:', { statsModalOpen, statsModalType, statsModalTitle, statsModalCases: statsModalCases.length });
  }, [statsModalOpen, statsModalType, statsModalTitle, statsModalCases]);

  const fetchCases = async () => {
    // Processos novos vão para o Portal do Cliente
    requestPortalSync();
    try {
      // Buscar casos regulares com clientes relacionados
      const { data: casesData, error: casesError } = await supabase
        .from('cases')
        .select(`
          *,
          case_clients (
            client_id,
            is_primary,
            clients (
              id,
              name,
              email
            )
          )
        `)
        .order('created_at', { ascending: false });
      
      // Buscar processos administrativos
      const { data: adminProcessesData, error: adminError } = await supabase
        .from('administrative_processes')
        .select('*, administrative_agency, procedure_type, process_number, fee_configuration')
        .order('created_at', { ascending: false });
      
      if (casesError) {
        console.error('Erro ao buscar casos:', casesError);
        throw casesError;
      }
      
      if (adminError) {
        console.error('Erro ao buscar processos administrativos:', adminError);
        throw adminError;
      }
      
      // Filtrar processos arquivados no frontend
      const activeCases = (casesData || []).filter(case_ => {
        // Se a coluna archived não existir, mostrar todos os processos
        return case_.archived === undefined ? true : !case_.archived;
      });
      
      // Combinar casos regulares com processos administrativos
      const allProcesses = [
        ...activeCases.map(case_ => ({
          ...case_,
          process_type: 'case',
          display_title: case_.title,
          display_type: case_.case_type || 'Processo Judicial'
        })),
        ...(adminProcessesData || []).filter(process => !process.archived).map(process => ({
          ...process,
          process_type: 'administrative',
          display_title: process.title || process.description,
          display_type: 'Processo Administrativo',
          case_type: 'Processo Administrativo',
          title: process.title || process.description
        }))
      ];
      
      // Processar todos os processos para incluir informações de clientes
      const processedCases = allProcesses.map(case_ => {
        // Para casos regulares, extrair clientes da relação case_clients
        let relatedClients = [];
        if (case_.case_clients && Array.isArray(case_.case_clients)) {
          relatedClients = case_.case_clients.map(cc => cc.clients).filter(Boolean);
        }
        
        // Se não há clientes relacionados via case_clients, usar o client_id direto
        if (relatedClients.length === 0 && case_.client_id) {
          const client = clients.find(c => c.id === case_.client_id);
          if (client) {
            relatedClients = [client];
          }
        }
        
        return {
          ...case_,
          relatedClients: relatedClients,
          // Manter compatibilidade com código existente
          clients: relatedClients.length > 0 ? relatedClients[0] : null
        };
      });
      
      setCases(processedCases as any);
      setLoading(false);
    } catch (error: any) {
      console.error('Erro ao carregar processos:', error);
      toast({ title: "Erro", description: "Erro ao carregar processos", variant: "destructive" });
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase.from('clients').select('id, name, email, cpf_cnpj, phone, mobile').order('name');
      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  // Função para criar lançamentos financeiros se houver configuração de contrato
  const createFinancialEntriesIfConfigured = async (caseId: string, clientId: string | null, userId: string) => {
    try {
      const contractConfig = (formData as any).contractConfiguration;
      if (!contractConfig) return;
      if ((formData as any).feeConfiguration?.isProBono) return;

      // Ao editar sem mexer no contrato, preserva os lançamentos existentes
      // (inclusive os já marcados como pagos ou ajustados no Financeiro)
      if (editingCase) {
        const saved = (editingCase as any).contractconfiguration;
        if (saved && JSON.stringify(saved) === JSON.stringify(contractConfig)) return;
      }

      // Contrato alterado: recria os lançamentos deste processo
      const { error: deleteError } = await supabase
        .from('financial_entries')
        .delete()
        .eq('case_id', caseId);

      if (deleteError) {
        console.warn('Erro ao remover lançamentos existentes:', deleteError);
      }

      // Criar lançamentos financeiros baseados na configuração
      const financialEntries = await createFinancialEntriesFromContract(
        contractConfig,
        caseId,
        clientId,
        userId,
        'monthly'
      );

      if (financialEntries.length > 0) {
        await saveFinancialEntries(financialEntries);
        toast({
          title: "Lançamentos Criados!",
          description: `${financialEntries.length} lançamentos financeiros foram criados automaticamente.`,
        });
      }
    } catch (error) {
      console.error('Erro ao criar lançamentos financeiros:', error);
      toast({
        title: "Aviso",
        description: "Processo salvo, mas houve erro ao criar lançamentos financeiros",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação: pelo menos um cliente deve ser selecionado
    if (formData.selected_clients.length === 0) {
      toast({ 
        title: "Erro de validação", 
        description: "Pelo menos um cliente deve ser selecionado", 
        variant: "destructive" 
      });
      return;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');
      
      // Preparar dados do caso (remover selected_clients que não é uma coluna da tabela)
      const { selected_clients, ...caseDataWithoutClients } = formData;
      
      // Determinar client_id baseado nos clientes selecionados
      let clientId = null;
      if (formData.selected_clients.length > 0) {
        const primaryClient = formData.selected_clients.find(client => client.is_primary) || formData.selected_clients[0];
        if (primaryClient && primaryClient.id && primaryClient.id.trim() !== '') {
          clientId = primaryClient.id;
        }
      }

      // Limpar campos vazios para evitar conflitos - apenas campos que existem na tabela cases
      const cleanCaseData = { 
        ...caseDataWithoutClients, 
        user_id: user.id, 
        is_litisconsorcio: formData.is_litisconsorcio,
        deadline: formData.deadline?.toISOString().split('T')[0] || null, 
        filing_date: formData.filing_date?.toISOString().split('T')[0] || null, 
        next_hearing: formData.next_hearing?.toISOString() || null, 
        statute_limitations: formData.statute_limitations?.toISOString().split('T')[0] || null, 
        case_value: formData.case_value
          ? parseBRLStringToNumber(formData.case_value)
          : null,
        client_fee: formData.client_fee ? parseFloat(formData.client_fee) / 100 : null, 
        court_costs: formData.court_costs ? parseFloat(formData.court_costs) / 100 : null, 
        success_fee: formData.success_fee ? parseFloat(formData.success_fee) : null,
        client_id: clientId,
        // Garantir que campos de texto vazios sejam null
        case_number: formData.case_number?.trim() || null,
        case_number_cnj: formData.case_number_cnj?.trim() || null,
        internal_number: formData.internal_number?.trim() || null,
        title: formData.title?.trim() || '',
        description: formData.description?.trim() || null,
        area_of_law: formData.area_of_law?.trim() || null,
        subject_matter: formData.subject_matter?.trim() || null,
        court: formData.court?.trim() || null,
        jurisdiction: formData.jurisdiction?.trim() || null,
        vara: formData.vara?.trim() || null,
        forum: formData.forum?.trim() || null,
        phase: formData.phase?.trim() || null,
        lawyer_responsible: formData.lawyer_responsible?.trim() || null,
        opposing_party: formData.opposing_party?.trim() || null,
        opposing_counsel: formData.opposing_counsel?.trim() || null,
        estimated_duration: formData.estimated_duration?.trim() || null,
        strategy_notes: formData.strategy_notes?.trim() || null,
        internal_notes: formData.internal_notes?.trim() || null,
        client_objectives: formData.client_objectives?.trim() || null,
        archive_reason: formData.archive_reason?.trim() || null,
        // Corrigir nomes das colunas para minúsculas (como estão no banco)
        contractconfiguration: (formData as any).contractConfiguration || null,
        feeconfiguration: (formData as any).feeConfiguration || null
      };

      // Remover campos financeiros avançados que não existem na tabela cases
      const { 
        financial_responsible, 
        payment_method, 
        installment_count, 
        installment_frequency, 
        first_payment_date, 
        late_payment_interest, 
        early_payment_discount, 
        payment_conditions, 
        collection_notes, 
        phase_fees, 
        success_fee_type, 
        success_fee_conditions, 
        success_fee_trigger,
        // Remover versões camelCase (usar apenas minúsculas)
        feeConfiguration,
        contractConfiguration,
        ...validCaseData 
      } = cleanCaseData;

      const caseData = validCaseData;

      let caseId: string;

      if (editingCase) {
        // Atualizar caso existente - apenas atualizar os campos modificados
        
        // Remover campos que podem causar conflito de chave única se não foram modificados
        const updateData = { ...caseData };
        
        // SEMPRE remover case_number e case_number_cnj da atualização para evitar conflitos
        delete updateData.case_number;
        delete updateData.case_number_cnj;

        const { error: caseError } = await supabase
          .from('cases')
          .update(updateData as any)
          .eq('id', editingCase.id);
        
        if (caseError) throw caseError;
        
        caseId = editingCase.id;
        await createFinancialEntriesIfConfigured(caseId, clientId, user.id);
        
        toast({ title: "Sucesso", description: "Processo atualizado com sucesso" });
      } else {
        // Criar novo caso
        // Verificar se já existe um processo com o mesmo case_number
        if (caseData.case_number) {
          const { data: existingCase } = await supabase
            .from('cases')
            .select('id')
            .eq('case_number', caseData.case_number)
            .single();
          
          if (existingCase) {
            toast({ 
              title: "Erro de validação", 
              description: "Já existe um processo com este número de processo", 
              variant: "destructive" 
            });
            return;
          }
        }
        
        // Verificar se já existe um processo com o mesmo case_number_cnj
        if (caseData.case_number_cnj) {
          const { data: existingCase } = await supabase
            .from('cases')
            .select('id')
            .eq('case_number_cnj', caseData.case_number_cnj)
            .single();
          
          if (existingCase) {
            toast({ 
              title: "Erro de validação", 
              description: "Já existe um processo com este número CNJ", 
              variant: "destructive" 
            });
            return;
          }
        }
        
        const { data: newCase, error: caseError } = await supabase
          .from('cases')
          .insert([caseData as any])
          .select('id')
          .single();
        
        if (caseError) throw caseError;
        caseId = newCase.id;
        
        // Criar lançamentos financeiros se houver configuração de contrato
        await createFinancialEntriesIfConfigured(caseId, clientId, user.id);
        
        toast({ title: "Sucesso", description: "Processo criado com sucesso" });
      }

      // Gerenciar clientes do caso
      if (formData.selected_clients.length > 0) {
        if (formData.is_litisconsorcio) {
          // Para litisconsórcio, usar função RPC para salvar todos os clientes
          console.log('Salvando múltiplos clientes para litisconsórcio:', formData.selected_clients);
          
          try {
            // Usar função RPC para adicionar cada cliente
            for (const client of formData.selected_clients) {
              // Verificar se o cliente tem ID válido
              if (!client.id || client.id.trim() === '') {
                console.warn('Cliente sem ID válido:', client);
                continue;
              }
              
              const { error } = await supabase
                .rpc('add_client_to_case' as any, {
                  case_id_param: caseId,
                  client_id_param: client.id,
                  is_primary_param: client.is_primary
                });
              
              if (error) {
                console.error('Erro ao adicionar cliente:', error);
                throw new Error(`Erro ao adicionar cliente ${client.name}`);
              }
            }
          } catch (error: any) {
            console.error('Erro ao salvar clientes:', error);
            throw new Error('Erro ao salvar clientes do processo');
          }
        } else {
          // Para casos não-litisconsórcio, salvar apenas o cliente principal
          const primaryClient = formData.selected_clients.find(client => client.is_primary) || formData.selected_clients[0];
          
          console.log('Salvando cliente principal:', primaryClient);
          
          // Verificar se o cliente tem ID válido
          if (primaryClient && primaryClient.id && primaryClient.id.trim() !== '') {
            await supabase
              .from('cases')
              .update({ client_id: primaryClient.id })
              .eq('id', caseId);
          } else {
            console.warn('Cliente sem ID válido:', primaryClient);
          }
        }
      }

      // Integração Financeira com tabela financial_entries
      try {
        const financialData = {
          client_fee: formData.client_fee,
          court_costs: formData.court_costs,
          success_fee: formData.success_fee,
          collection_notes: formData.collection_notes,
          phase_fees: formData.phase_fees,
          success_fee_type: formData.success_fee_type,
          success_fee_conditions: formData.success_fee_conditions
        };

        if (!editingCase) {
          // Criar entradas financeiras para novo caso
          await FinancialEntriesService.createCaseFinancialEntries(
            caseId,
            clientId,
            financialData,
            user.id
          );
          toast({ title: "Sucesso", description: "Processo criado e dados financeiros integrados com sucesso" });
        } else {
          toast({ title: "Sucesso", description: "Processo atualizado com sucesso" });
        }
      } catch (financialError) {
        console.error('Erro na integração financeira:', financialError);
        // Não falhar o processo principal, apenas mostrar aviso
        toast({ 
          title: "Aviso", 
          description: "Processo salvo, mas houve erro na integração financeira", 
          variant: "destructive" 
        });
      }

      setIsDialogOpen(false);
      setEditingCase(null);
      resetForm();
      fetchCases();
      if (fromIntimacaoRef.current && !editingCase) {
        fromIntimacaoRef.current = false;
        navigate(`/processo/${caseId}`);
      }
    } catch (error: any) {
      console.error('Erro ao salvar processo:', error);
      toast({ title: "Erro", description: error.message || "Não foi possível salvar o processo", variant: "destructive" });
    }
  };

  const handleAdministrativeProcessSubmit = async (data: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Preparar dados para o processo administrativo
      const administrativeCaseData = {
        title: data.title,
        description: data.description,
        case_number: data.administrative_number,
        case_type: 'Processo Administrativo',
        area_of_law: 'Administrativo',
        subject_matter: data.subject_matter,
        status: data.status,
        priority: data.priority,
        client_id: data.client_id,
        filing_date: data.filing_date || null,
        deadline: data.deadline || null,
        estimated_duration: data.estimated_duration,
        internal_notes: data.internal_notes,
        tags: data.tags,
        is_administrative_process: true,
        administrative_agency: data.administrative_agency,
        administrative_procedure: data.administrative_procedure,
        user_id: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('cases')
        .insert([administrativeCaseData]);

      if (error) throw error;

      toast({ 
        title: "Sucesso!", 
        description: "Processo administrativo cadastrado com sucesso!" 
      });

      setIsAdministrativeModalOpen(false);
      fetchCases();
    } catch (error: any) {
      console.error('Erro ao salvar processo administrativo:', error);
      toast({ 
        title: "Erro", 
        description: error.message || "Erro ao cadastrar processo administrativo", 
        variant: "destructive" 
      });
    }
  };

  const resetForm = () => { 
    setFormData({ 
      case_number: "", 
      case_number_cnj: "", 
      internal_number: "", 
      title: "", 
      description: "", 
      case_type: "", 
      area_of_law: "", 
      subject_matter: "", 
      case_value: "", 
      risk_classification: "medio", 
      court: "", 
      court_level: "1_instancia", 
      jurisdiction: "", 
      vara: "", 
      forum: "", 
      filing_date: undefined, 
      deadline: undefined, 
      next_hearing: undefined, 
      statute_limitations: undefined, 
      status: "Em andamento", 
      phase: "", 
      priority: "Média", 
      confidentiality_level: "normal", 
      lawyer_responsible: "", 
      client_id: null, 
      is_litisconsorcio: false, 
      selected_clients: [], 
      opposing_party: "", 
      opposing_counsel: "", 
      client_fee: "", 
      court_costs: "", 
      estimated_duration: "", 
      success_fee: "", 
      strategy_notes: "", 
      internal_notes: "", 
      client_objectives: "", 
      monitoring_deadlines: true, 
      critical_deadlines: [], 
      alert_days_before: 5, 
      tags: [], 
      archived: false, 
      archive_reason: "",
      // Novos campos financeiros
      financial_responsible: "",
      payment_method: "a_vista",
      installment_count: 1,
      installment_frequency: "mensal",
      first_payment_date: undefined,
      late_payment_interest: "",
      early_payment_discount: "",
      payment_conditions: "",
      collection_notes: "",
      phase_fees: [],
      success_fee_type: "percentage",
      success_fee_conditions: "",
      success_fee_trigger: "sentenca"
    }); 
  };
  
  const handleEdit = async (case_: Case) => {
    setEditingCase(case_);
    
    // Verificar se é processo administrativo e abrir o modal correto
    if (case_.process_type === 'administrative') {
      setIsAdministrativeModalOpen(true);
      return;
    }
    
    // Carregar clientes relacionados
    let selectedClients: Array<{ id: string; name: string; email: string; is_primary: boolean }> = [];
    
    if (case_.is_litisconsorcio) {
      // Para litisconsórcio, tentar carregar da tabela case_clients
      // Por enquanto, usar apenas o client_id da tabela cases
      if (case_.client_id) {
        const client = clients.find(c => c.id === case_.client_id);
        if (client) {
          selectedClients = [{ ...client, is_primary: true }];
        }
      }
    } else {
      // Para casos não-litisconsórcio, usar o client_id da tabela cases
      if (case_.client_id) {
        const client = clients.find(c => c.id === case_.client_id);
        if (client) {
          selectedClients = [{ ...client, is_primary: true }];
        }
      }
    }
    
    setFormData({ 
      case_number: case_.case_number || "", 
      case_number_cnj: case_.case_number_cnj || "", 
      internal_number: case_.internal_number || "", 
      title: case_.title || "", 
      description: case_.description || "", 
      case_type: case_.case_type || "", 
      area_of_law: case_.area_of_law || "", 
      subject_matter: case_.subject_matter || "", 
      case_value: numberToBRLInputFormat(case_.case_value), 
      risk_classification: case_.risk_classification || "medio", 
      court: case_.court || "", 
      court_level: case_.court_level || "1_instancia", 
      jurisdiction: case_.jurisdiction || "", 
      vara: case_.vara || "", 
      forum: case_.forum || "", 
      filing_date: case_.filing_date ? new Date(case_.filing_date) : undefined, 
      deadline: case_.deadline ? new Date(case_.deadline) : undefined, 
      next_hearing: case_.next_hearing ? new Date(case_.next_hearing) : undefined, 
      statute_limitations: case_.statute_limitations ? new Date(case_.statute_limitations) : undefined, 
      status: case_.status, 
      phase: case_.phase || "", 
      priority: case_.priority, 
      confidentiality_level: case_.confidentiality_level || "normal", 
      lawyer_responsible: case_.lawyer_responsible || "", 
      client_id: case_.client_id || null, 
      is_litisconsorcio: case_.is_litisconsorcio || false, 
      selected_clients: selectedClients,
      opposing_party: case_.opposing_party || "", 
      opposing_counsel: case_.opposing_counsel || "", 
      client_fee: case_.client_fee?.toString() || "", 
      court_costs: case_.court_costs?.toString() || "", 
      estimated_duration: case_.estimated_duration || "", 
      success_fee: case_.success_fee?.toString() || "", 
      strategy_notes: case_.strategy_notes || "", 
      internal_notes: case_.internal_notes || "", 
      client_objectives: case_.client_objectives || "", 
      monitoring_deadlines: case_.monitoring_deadlines ?? true, 
      critical_deadlines: case_.critical_deadlines || [], 
      alert_days_before: case_.alert_days_before || 5, 
      tags: case_.tags || [], 
      archived: case_.archived || false, 
      archive_reason: case_.archive_reason || "",
      // Campos financeiros básicos (que existem na tabela cases)
      financial_responsible: "",
      payment_method: "a_vista" as 'a_vista' | 'parcelado' | 'condicional' | 'por_fase',
      installment_count: 1,
      installment_frequency: "mensal" as 'mensal' | 'bimestral' | 'trimestral' | 'semestral' | 'anual',
      first_payment_date: undefined,
      late_payment_interest: "",
      early_payment_discount: "",
      payment_conditions: "",
      collection_notes: "",
      phase_fees: [] as Array<{phase: string; amount: string; due_date?: Date; description: string}>,
      success_fee_type: "percentage" as 'percentage' | 'fixed' | 'conditional',
      success_fee_conditions: "",
      success_fee_trigger: "sentenca" as 'sentenca' | 'acordo' | 'liminar' | 'outro',
      // Carregar configurações financeiras salvas
      contractConfiguration: (case_ as any).contractconfiguration || null,
      feeConfiguration: (case_ as any).feeconfiguration || null
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      // Identificar se é processo judicial ou administrativo
      const processToDelete = cases.find(c => c.id === id);
      
      // Verificar lançamentos financeiros antes de excluir
      const financialField = processToDelete?.process_type === 'administrative' 
        ? 'administrative_process_id' 
        : 'case_id';
      
      const { data: financialEntries, error: fetchError } = await supabase
        .from('financial_entries')
        .select('*')
        .eq(financialField, id);
      
      if (fetchError) {
        console.error('Erro ao buscar lançamentos:', fetchError);
      }
      
      // Contar lançamentos pendentes e confirmados
      const pendingEntries = financialEntries?.filter(entry => 
        entry.status === 'Pendente' // Apenas pendentes (futuros) serão excluídos
      ) || [];
      
      const confirmedOrOverdueEntries = financialEntries?.filter(entry => 
        entry.status === 'Pago' || 
        entry.payment_confirmed === true ||
        entry.status === 'Vencido' // Vencidos serão mantidos para exclusão manual
      ) || [];
      
      // Construir mensagem de aviso
      let confirmMessage = 'Tem certeza que deseja excluir este processo?\n\n';
      
      if (financialEntries && financialEntries.length > 0) {
        confirmMessage += `⚠️ ATENÇÃO: Este processo possui ${financialEntries.length} lançamento(s) financeiro(s):\n\n`;
        
        if (pendingEntries.length > 0) {
          confirmMessage += `✓ ${pendingEntries.length} lançamento(s) PENDENTE(S) será(ão) EXCLUÍDO(S)\n`;
        }
        
        if (confirmedOrOverdueEntries.length > 0) {
          const vencidosCount = financialEntries.filter(e => e.status === 'Vencido').length;
          const pagosCount = financialEntries.filter(e => e.status === 'Pago' || e.payment_confirmed === true).length;
          
          if (vencidosCount > 0) {
            confirmMessage += `✓ ${vencidosCount} lançamento(s) VENCIDO(S) será(ão) MANTIDO(S) para exclusão manual\n`;
          }
          if (pagosCount > 0) {
            confirmMessage += `✓ ${pagosCount} lançamento(s) PAGO(S) será(ão) MANTIDO(S)\n`;
          }
        }
        
        confirmMessage += '\n💡 Dica: Verifique os lançamentos na página Financeiro antes de prosseguir.';
      }
      
      if (!confirm(confirmMessage)) return;
      
      if (processToDelete?.process_type === 'administrative') {
        // Para processos administrativos:
        // 1. Remover apenas entradas financeiras PENDENTES
        if (pendingEntries.length > 0) {
          const pendingIds = pendingEntries.map(e => e.id);
          const { error: financialError } = await supabase
            .from('financial_entries')
            .delete()
            .in('id', pendingIds);
          
          if (financialError) {
            console.error('Erro ao remover entradas financeiras:', financialError);
          }
        }
        
        // 2. Remover prazos associados
        const { error: deadlinesError } = await supabase
          .from('administrative_process_deadlines')
          .delete()
          .eq('process_id', id);
        
        if (deadlinesError) {
          console.error('Erro ao remover prazos:', deadlinesError);
        }
        
        // 3. Finalmente remover o processo administrativo
        const { error } = await supabase.from('administrative_processes').delete().eq('id', id);
        if (error) throw error;
      } else {
        // Para processos judiciais:
        // 1. Remover apenas entradas financeiras PENDENTES
        if (pendingEntries.length > 0) {
          const pendingIds = pendingEntries.map(e => e.id);
          const { error: financialError } = await supabase
            .from('financial_entries')
            .delete()
            .in('id', pendingIds);
          
          if (financialError) {
            console.error('Erro ao remover entradas financeiras:', financialError);
          }
        }
        
        // 2. Remover processo judicial
        const { error } = await supabase.from('cases').delete().eq('id', id);
        if (error) throw error;
      }
      
      let successMessage = 'Processo excluído com sucesso';
      if (pendingEntries.length > 0) {
        successMessage += ` e ${pendingEntries.length} lançamento(s) pendente(s) removido(s)`;
      }
      if (confirmedOrOverdueEntries.length > 0) {
        const vencidosCount = financialEntries?.filter(e => e.status === 'Vencido').length || 0;
        const pagosCount = financialEntries?.filter(e => e.status === 'Pago' || e.payment_confirmed === true).length || 0;
        
        if (vencidosCount > 0 || pagosCount > 0) {
          successMessage += `. ${vencidosCount + pagosCount} lançamento(s) mantido(s)`;
        }
      }
      
      toast({ 
        title: "Sucesso", 
        description: successMessage 
      });
      fetchCases();
    } catch (error) {
      console.error('Erro ao excluir processo:', error);
      toast({ title: "Erro", description: "Não foi possível excluir o processo", variant: "destructive" });
    }
  };

  const handleAddEvent = (case_: Case) => {
    setSelectedCaseForEvent(case_);
    setIsEventModalOpen(true);
  };

  const handleCaseAnalysis = async (case_: Case) => {
    setSelectedCase(case_);
    setActiveTab("ai-analysis");
    // const clientName = case_.clients && case_.clients.length > 0 ? (case_.clients[0] as any).name : 'Não informado';
    // const analysis = await analyzeCase({ ...case_, client_name: clientName }); // Removed useAI hook
    // if (analysis) { setAiAnalysis(analysis); } // Removed useAI hook
  };

  const handleImportDatajud = (proc: any) => { // Removed ProcessData import
    toast({ title: "Processo do Datajud carregado", description: "Revise os dados e salve para criar o processo." });
    const opposingParty = (proc.partes || []).find(p => p.polo === 'PASSIVO');
    const mainSubject = (proc.assunto && proc.assunto.length > 0) ? proc.assunto[0].nome : 'Não especificado';
    const mappedDataForForm = { ...formData, title: `Processo sobre ${proc.classe.nome}`, case_number_cnj: proc.numeroProcesso, description: `Processo importado via Datajud. Classe: ${proc.classe.nome}. Assunto principal: ${mainSubject}.`, court: proc.orgaoJulgador?.nome || 'Não informado', case_type: proc.classe.nome, subject_matter: mainSubject, filing_date: proc.dataAjuizamento ? new Date(proc.dataAjuizamento) : undefined, case_value: proc.valorCausa != null && proc.valorCausa !== '' ? numberToBRLInputFormat(Number(proc.valorCausa)) : "", opposing_party: opposingParty?.nome || '', status: "Em andamento", priority: "Média", datajud_json: proc, datajud_monitorando: false, };
    setFormData(mappedDataForForm as any);
    setEditingCase(null);
    setIsDialogOpen(true);
  };

  const handleMonitorDatajud = async (proc: any) => { // Removed ProcessData import
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { 
        toast({ title: "Erro de Autenticação", description: "Usuário não encontrado.", variant: "destructive" }); 
        return; 
      }

      // Verificar se o processo já existe
      const { data: existingCase } = await supabase
        .from('cases')
        .select('id')
        .eq('case_number_cnj', proc.numeroProcesso)
        .eq('user_id', user.id)
        .single();

      if (existingCase) {
        toast({ 
          title: "Processo já existe", 
          description: "Este processo já está na sua lista.", 
          variant: "destructive" 
        });
        return;
      }

      const opposingParty = (proc.partes || []).find(p => p.polo === 'PASSIVO');
      const mainSubject = (proc.assunto && proc.assunto.length > 0) ? proc.assunto[0].nome : 'Não especificado';
      
      const dataToSave = { 
        user_id: user.id, 
        title: `Processo sobre ${proc.classe.nome}`, 
        case_number_cnj: proc.numeroProcesso, 
        description: `Processo importado para monitoramento. Assunto: ${mainSubject}.`, 
        court: proc.orgaoJulgador?.nome || 'Não informado', 
        case_type: proc.classe.nome, 
        subject_matter: mainSubject, 
        filing_date: proc.dataAjuizamento, 
        case_value: proc.valorCausa, 
        opposing_party: opposingParty?.nome || '', 
        status: "Em andamento", 
        priority: "Média", 
        datajud_monitorando: true, 
        datajud_json: proc 
      };

      // Inserir o processo
      const { data: newCase, error: caseError } = await supabase
        .from('cases')
        .insert([dataToSave])
        .select()
        .single();
      
      if (caseError) { 
        console.error("Erro do Supabase ao salvar processo:", caseError); 
        throw new Error(`Erro do Supabase: ${caseError.message}`); 
      }

                     // Salvar os andamentos se existirem
               if (proc.movimentos && proc.movimentos.length > 0 && newCase) {
                 const movementsToSave = proc.movimentos.map((mov: any) => ({
                   case_id: newCase.id,
                   movement_date: mov.dataHora || new Date().toISOString(),
                   description: mov.nome || 'Movimentação processual',
                   details: mov.complemento || null,
                   source: 'datajud',
                   external_id: `${proc.numeroProcesso}_${mov.dataHora}`,
                   created_by: user.id
                 }));
         
                 const { error: movementsError } = await supabase
                   .from('case_movements' as any)
                   .insert(movementsToSave);
         
                 if (movementsError) {
                   console.error("Erro ao salvar movimentações:", movementsError);
                   // Não vamos falhar o processo todo se as movimentações falharem
                 } else {
                   console.log(`Salvos ${movementsToSave.length} andamentos do Datajud`);
                 }
               }
      
                     const andamentosSalvos = proc.movimentos?.length || 0;
               toast({ 
                 title: "Processo salvo!", 
                 description: `Processo importado com sucesso. ${andamentosSalvos} andamentos salvos automaticamente.` 
               });
      fetchCases();
    } catch (error: any) {
      toast({ title: "Erro ao Salvar", description: error.message, variant: "destructive" });
    }
  };

  const handleSyncProcess = async (caseId: string) => {
    setSyncingCases(prev => new Set(prev).add(caseId));
    try {
      // Implementação da sincronização com Datajud
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulação
      toast({ title: "Sucesso!", description: "Processo sincronizado com Datajud." });
    } catch (error) {
      toast({ title: "Erro", description: "Erro ao sincronizar processo.", variant: "destructive" });
    } finally {
      setSyncingCases(prev => {
        const next = new Set(prev);
        next.delete(caseId);
        return next;
      });
    }
  };

  // Funções para abrir modais de estatísticas
  const openStatsModal = (type: string, title: string, filteredCases: Case[]) => {
    console.log('openStatsModal chamado:', type, title, filteredCases.length);
    setStatsModalType(type);
    setStatsModalTitle(title);
    setStatsModalCases(filteredCases);
    setStatsModalOpen(true);
  };

  const getCriticalDeadlines = () => {
    return cases.filter(c => {
      if (!c.deadline) return false;
      const days = Math.ceil((new Date(c.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return days <= 7 && days >= 0;
    });
  };

  const getHighRiskCases = () => {
    return cases.filter(c => c.risk_classification === 'alto' || c.risk_classification === 'critico');
  };

  const getNewThisMonth = () => {
    return cases.filter(c => {
      const caseDate = new Date(c.created_at);
      const now = new Date();
      return caseDate.getMonth() === now.getMonth() && caseDate.getFullYear() === now.getFullYear();
    });
  };

  const filteredCases = cases.filter(case_ => {
    // Garantir que clients seja sempre um array
    const clientsArray = Array.isArray(case_.clients) ? case_.clients : [];
    const clientNames = clientsArray.map(client => (client as any).name || '').join(' ');
    const searchMatch = (
      (case_.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (case_.case_number_cnj || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientNames.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const statusMatch = statusFilter === "all" || case_.status === statusFilter;
    
    let typeMatch = true;
    if (processTypeFilter !== "all") {
      typeMatch = processTypeFilter === "administrative" ? 
        case_.process_type === 'administrative' : 
        case_.process_type !== 'administrative';
    }
    
    let courtLevelMatch = true;
    if (courtLevelFilter !== "all") {
      courtLevelMatch = case_.court_level === courtLevelFilter;
    }
    
    let clientMatch = true;
    if (clientFilter !== "all") {
      // Verificar se o cliente está relacionado ao caso (principal ou litisconsórcio)
      const hasClient = case_.relatedClients && case_.relatedClients.some((client: any) => client.id === clientFilter);
      clientMatch = hasClient || false;
    }
    
    return searchMatch && statusMatch && typeMatch && courtLevelMatch && clientMatch;
  });

  // Indicador clicado (Em atraso, Próximos 7 dias...) filtra a lista junto com a barra de filtros
  const activeKpi = kpiKey ? getCaseKpis(cases).find(k => k.key === kpiKey) || null : null;
  const caseKey = (c: any) => `${c.process_type}-${c.id}`;
  const kpiIds = activeKpi ? new Set(activeKpi.cases.map(caseKey)) : null;
  const hasNewMovement = (c: any) => !!c.movement_update_at && (!c.movement_seen_at || new Date(c.movement_seen_at) < new Date(c.movement_update_at));
  const updatedCount = cases.filter(hasNewMovement).length;
  const kpiFiltered = kpiIds ? filteredCases.filter(c => kpiIds.has(caseKey(c))) : filteredCases;
  // Movimentação nova sempre no topo
  const displayedCases = (onlyUpdated ? kpiFiltered.filter(hasNewMovement) : kpiFiltered)
    .map((c, index) => ({ c, index }))
    .sort((a, b) => Number(hasNewMovement(b.c)) - Number(hasNewMovement(a.c)) || a.index - b.index)
    .map(({ c }) => c);

  const administrativeCount = cases.filter(c => c.process_type === 'administrative').length;
  const judicialCount = cases.length - administrativeCount;

  const courtLevelLabels: Record<string, string> = {
    '1_instancia': '1ª Instância',
    '2_instancia': '2ª Instância',
    superior: 'Tribunais Superiores',
    supremo: 'Supremo Tribunal Federal',
  };

  const activeFilters = [
    searchTerm && { key: 'search', label: `“${searchTerm}”`, clear: () => setSearchTerm('') },
    statusFilter !== 'all' && { key: 'status', label: statusFilter, clear: () => setStatusFilter('all') },
    processTypeFilter !== 'all' && {
      key: 'type',
      label: processTypeFilter === 'judicial' ? 'Judiciais' : 'Administrativos',
      clear: () => setProcessTypeFilter('all'),
    },
    courtLevelFilter !== 'all' && {
      key: 'court',
      label: courtLevelLabels[courtLevelFilter] || courtLevelFilter,
      clear: () => setCourtLevelFilter('all'),
    },
    clientFilter !== 'all' && {
      key: 'client',
      label: clients.find(c => c.id === clientFilter)?.name || 'Cliente',
      clear: () => setClientFilter('all'),
    },
    activeKpi && { key: 'kpi', label: activeKpi.label, clear: () => setKpiKey(null) },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setProcessTypeFilter('all');
    setCourtLevelFilter('all');
    setClientFilter('all');
    setKpiKey(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4"><div className="h-8 bg-muted rounded w-1/4"></div><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{[1, 2, 3].map(i => (<div key={i} className="h-48 bg-muted rounded"></div>))}</div></div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 p-4 md:p-6">
      {/* Cabeçalho */}
      <header className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Gestão processual</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Processos</h1>
          <p className="mt-1 text-sm text-slate-500">
            {judicialCount} judicia{judicialCount === 1 ? 'l' : 'is'} · {administrativeCount} administrativo{administrativeCount === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
          {/* Ferramentas */}
          <div className="flex items-center gap-0.5 rounded-xl border border-slate-200 bg-white p-0.5">
            <button type="button" onClick={() => navigate('/archived-cases')} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-[#faf7f2] hover:text-[#7a6440]" title="Processos arquivados" aria-label="Processos arquivados">
              <Archive className="h-4 w-4" />
            </button>
            <EmailCaseSyncButton onSynced={fetchCases} iconOnly />
            <DatajudSearchModal
              onProcessImport={handleImportDatajud}
              onProcessMonitor={handleMonitorDatajud}
              iconOnly
            />
            <ImportOabProcessesModal onImported={fetchCases} iconOnly />
          </div>
          <Button
            onClick={() => setIsAdministrativeModalOpen(true)}
            className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-[13px] text-slate-800 shadow-none hover:border-[#c4ab83] hover:bg-[#faf7f2] hover:text-slate-900"
          >
            <Plus className="mr-1.5 h-4 w-4 text-[#9a7f52]" />
            Novo processo administrativo
          </Button>
          <Button onClick={() => setIsDialogOpen(true)} className="h-10 rounded-xl bg-slate-900 px-4 text-[13px] text-white shadow-sm hover:bg-slate-800">
            <Plus className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />
            Novo processo judicial
          </Button>
        </div>
      </header>

      {(updatedCount > 0 || onlyUpdated) && (
        <button
          type="button"
          onClick={() => setOnlyUpdated(v => !v)}
          className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm transition-colors ${onlyUpdated ? 'border-[#c4ab83] bg-[#faf7f2]' : 'border-[#c4ab83]/40 bg-white hover:bg-[#faf7f2]'}`}
        >
          <span className="flex items-center gap-2 text-slate-700">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#c4ab83] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#c4ab83]" />
            </span>
            <strong>{updatedCount}</strong> processo{updatedCount === 1 ? '' : 's'} com movimentação nova
          </span>
          <span className="text-xs font-semibold text-[#9a7f52]">{onlyUpdated ? 'Mostrar todos' : 'Ver só esses'}</span>
        </button>
      )}

      {/* Indicadores */}
      <CaseAnalytics cases={cases} activeKey={kpiKey} onSelect={(selection) => setKpiKey(selection ? selection.key : null)} />

      {/* Barra de filtros (fixa ao rolar) */}
      <div className="sticky top-2 z-20 rounded-2xl border border-slate-200/80 bg-white/85 p-2 shadow-sm backdrop-blur-md">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
          <div className="min-w-0 flex-1">
            <CasesFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              courtLevelFilter={courtLevelFilter}
              setCourtLevelFilter={setCourtLevelFilter}
              clientFilter={clientFilter}
              setClientFilter={setClientFilter}
              clients={clients}
            />
          </div>
          <div className="flex shrink-0 items-center justify-between gap-2">
            <div className="inline-flex rounded-xl bg-slate-100 p-1">
              {([
                { value: 'all', label: 'Todos', count: cases.length },
                { value: 'judicial', label: 'Judiciais', count: judicialCount },
                { value: 'administrative', label: 'Administrativos', count: administrativeCount },
              ] as const).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setProcessTypeFilter(option.value)}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-medium transition-all duration-200 ${
                    processTypeFilter === option.value
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {option.label}
                  <span className={`tabular-nums ${processTypeFilter === option.value ? 'text-[#9a7f52]' : 'text-slate-400'}`}>
                    {option.count}
                  </span>
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-xl bg-slate-100 p-1">
              {([
                { value: 'card', icon: LayoutGrid, label: 'Cartões' },
                { value: 'table', icon: List, label: 'Tabela' },
              ] as const).map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  type="button"
                  title={label}
                  onClick={() => setViewMode(value)}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200 ${
                    viewMode === value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Resumo da busca */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
          <span>
            <strong className="font-semibold text-slate-900">{displayedCases.length}</strong>
            {displayedCases.length !== cases.length && <> de {cases.length}</>} processo{displayedCases.length === 1 ? '' : 's'}
          </span>
          {activeFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              onClick={filter.clear}
              className="inline-flex max-w-[16rem] items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
              title="Remover filtro"
            >
              <span className="truncate">{filter.label}</span>
              <X className="h-3 w-3 shrink-0" />
            </button>
          ))}
          {activeFilters.length > 1 && (
            <button type="button" onClick={clearAllFilters} className="text-xs font-medium text-[#9a7f52] hover:underline">
              Limpar tudo
            </button>
          )}
        </div>
        <span className="text-xs text-slate-400">Mais recentes primeiro</span>
      </div>

      {/* Lista de processos */}
      {viewMode === 'card' ? (
        displayedCases.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center animate-in fade-in duration-300">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#faf7f2]">
              <Scale className="h-5 w-5 text-[#c4ab83]" />
            </div>
            <p className="font-medium text-slate-900">Nenhum processo encontrado</p>
            <p className="mt-1 text-sm text-slate-500">
              {activeFilters.length ? 'Tente remover algum filtro.' : 'Cadastre um processo ou importe pela sua OAB.'}
            </p>
            {activeFilters.length > 0 && (
              <Button variant="outline" size="sm" className="mt-4 rounded-xl" onClick={clearAllFilters}>
                Limpar filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2.5">
            {displayedCases.map((case_, index) => (
              <div
                key={case_.id}
                className="animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-300"
                style={{ animationDelay: `${Math.min(index, 12) * 35}ms` }}
              >
                <CaseListItem
                  case_={case_ as any}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onAnalyze={handleCaseAnalysis}
                  onSync={handleSyncProcess}
                  isSyncing={syncingCases.has(case_.id)}
                  onAddEvent={handleAddEvent}
                  allClients={clients} />
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-xs sm:text-sm">
            <thead className="bg-slate-50 text-left text-[0.68rem] uppercase tracking-[0.16em] text-slate-500">
              <tr>
                <th className="px-3 py-2">Processo</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Prazo</th>
                <th className="px-3 py-2">Valor</th>
                <th className="px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {displayedCases.map((case_) => {
                const deadlineInfoTable = getDeadlineInfo(case_.deadline as string);
                const clientNames = getConsorcioClientNames(case_);
                const primaryClientName = getPrimaryClientName(case_);
                const hasMultipleClients = case_.is_litisconsorcio && clientNames.length > 1;
                return (
                  <tr key={case_.id} className="hover:bg-slate-50">
                    <td className="px-3 py-3 align-top">
                      <div className="font-semibold text-slate-900">{case_.title || 'Processo sem título'}</div>
                      <div className="mt-1 text-[11px] text-slate-500">{formatProcessNumber(case_)}</div>
                    </td>
                    <td className="px-3 py-3 align-top text-slate-700">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="max-w-[12rem] truncate font-medium text-slate-900 sm:max-w-[16rem]">{primaryClientName}</span>
                        {hasMultipleClients && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 rounded-full p-0 text-slate-500 hover:bg-slate-100"
                            onClick={() => setConsorcioClientsCase(case_)}
                            aria-label="Mostrar todos os clientes do consórcio"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <Badge variant="outline" className="bg-slate-100 text-slate-700">
                        {case_.status || 'Em andamento'}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 align-top">
                      {deadlineInfoTable ? (
                        <span className={`font-semibold ${deadlineInfoTable.color}`}>{deadlineInfoTable.text}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">{formatCurrencyValue(case_.case_value)}</td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(case_)}>
                          Editar
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedCase(case_)}>
                          Ver
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={Boolean(consorcioClientsCase)} onOpenChange={(open) => { if (!open) setConsorcioClientsCase(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Clientes do consórcio</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Veja o nome de todos os clientes desse processo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            {consorcioClientsCase ? (
              (getConsorcioClientNames(consorcioClientsCase) || []).map((name, index) => (
                <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
                  {name}
                </div>
              ))
            ) : (
              <p>Nenhum cliente encontrado.</p>
            )}
          </div>
          <div className="mt-6 flex justify-end">
            <Button variant="outline" onClick={() => setConsorcioClientsCase(null)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {displayedCases.length === 0 && !loading && (
        <div className="text-center py-16 col-span-full">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Nenhum processo encontrado</h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== "all" 
                ? "Tente ajustar seus filtros de busca ou status" 
                : "Comece criando seu primeiro processo para organizar seus casos"
              }
            </p>
            {!searchTerm && statusFilter === "all" && (
              <Button onClick={() => setIsDialogOpen(true)} className="mx-auto">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Processo
              </Button>
            )}
          </div>
        </div>
      )}
      <CaseDetailModal case_={selectedCase} isOpen={!!selectedCase} onClose={() => setSelectedCase(null)} activeTab={activeTab} setActiveTab={setActiveTab} aiAnalysis={aiAnalysis} aiLoading={false} onAnalyzeCase={handleCaseAnalysis} onSync={handleSyncProcess} />
      {/* Modal de Criação/Edição de Processo */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[98vw] !max-w-[1440px] max-h-[92vh] overflow-y-auto bg-white shadow-2xl border border-[#c4ab83]/30 sm:rounded-2xl">
          <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
            {/* Monograma AS como marca d'água */}
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
            />
            <div className="relative flex items-center gap-4">
              <img
                src="/imgs/logos allana/letra_A_logo.png"
                alt="Allana Sthéfany"
                className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
              />
              <div className="text-left">
                <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                  {editingCase ? "Editar Processo" : "Novo Processo"}
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm text-slate-500">
                  {editingCase ? "Edite as informações do processo" : "Preencha as informações para criar um novo processo"}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          {(() => {
            // Cliente do processo com cadastro incompleto (sem CPF e sem telefone)
            const primary = formData.selected_clients.find((c) => c.is_primary) || formData.selected_clients[0];
            const full: any = primary && clients.find((c: any) => c.id === primary.id);
            if (!full || full.cpf_cnpj || full.phone || full.mobile) return null;
            return (
              <div className="mb-5 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-amber-900">
                  <strong>{full.name}</strong> está cadastrado só com o nome. Complete os dados do cliente (CPF, telefone, endereço...) sem sair daqui.
                </p>
                <Button
                  type="button"
                  onClick={() => setClientToComplete(full.id)}
                  className="h-9 shrink-0 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800"
                >
                  Completar cadastro do cliente
                </Button>
              </div>
            );
          })()}
          <EnhancedCaseForm
            formData={formData}
            setFormData={(data) => setFormData(data as any)}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsDialogOpen(false);
              resetForm();
            }}
            isEditing={!!editingCase}
            clients={clients}
          />
        </DialogContent>
      </Dialog>

      <ClientQuickEditDialog
        clientId={clientToComplete}
        onOpenChange={(open) => !open && setClientToComplete(null)}
        onSaved={(client) => {
          fetchClients();
          setFormData((current: any) => ({
            ...current,
            selected_clients: current.selected_clients.map((c: any) => (c.id === client.id ? { ...c, name: client.name, email: client.email } : c)),
          }));
        }}
      />

      <EventFormModal
        isOpen={isEventModalOpen}
        onOpenChange={setIsEventModalOpen}
        caseId={selectedCaseForEvent?.id}
        onSaveSuccess={() => {
            toast({ title: "Sucesso!", description: "Evento salvo no seu calendário." });
            // Futuramente, podemos recarregar a agenda aqui.
        }}
      />
      
      {/* Modal de Estatísticas */}
      <Dialog open={statsModalOpen} onOpenChange={setStatsModalOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto bg-white shadow-2xl border border-gray-200 z-[9999]">
          <DialogHeader className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 -m-6 mb-6 border-b border-gray-200">
            <DialogTitle className="flex items-center gap-2 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-blue-800 drop-shadow-lg">
              {statsModalType === 'critical' && <AlertTriangle className="w-5 h-5 text-destructive" />}
              {statsModalType === 'risk' && <Clock className="w-5 h-5 text-orange-500" />}
              {statsModalType === 'new' && <TrendingUp className="w-5 h-5 text-primary" />}
              {statsModalType === 'total' && <Scale className="w-5 h-5 text-muted-foreground" />}
              {statsModalTitle}
            </DialogTitle>
            <DialogDescription className="text-gray-600/90 mt-3 text-lg">
              Visualize os detalhes dos processos nesta categoria
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <span className="text-sm text-muted-foreground">
                Mostrando <strong>{statsModalCases.length}</strong> processos
              </span>
              {statsModalType === 'value' && (
                <span className="text-sm font-medium">
                  Valor Total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(statsModalCases.reduce((sum, c) => sum + (c.case_value || 0), 0))}
                </span>
              )}
            </div>
            
            <div className="space-y-3">
              {statsModalCases.length > 0 ? (
                statsModalCases.map((case_) => (
                  <Card key={case_.id} className="p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold">{case_.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {case_.case_number_cnj || case_.case_number || 'Sem número'}
                        </p>
                        {case_.deadline && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Prazo: {new Date(case_.deadline).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                        {case_.case_value && (
                          <p className="text-xs text-muted-foreground">
                            Valor: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(case_.case_value)}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => {
                          setSelectedCase(case_);
                          setStatsModalOpen(false);
                        }}>
                          Ver Detalhes
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Nenhum processo encontrado nesta categoria.</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Processo Administrativo */}
      <AdministrativeProcessModal
        isOpen={isAdministrativeModalOpen}
        onClose={() => setIsAdministrativeModalOpen(false)}
        onSuccess={() => {
          setIsAdministrativeModalOpen(false);
          fetchCases();
        }}
        editingCase={editingCase}
      />
      
      <div className="fixed bottom-6 right-6 z-50">
        <Button onClick={() => window.open('/ai-center', '_blank')} className="rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-all" size="icon"><Sparkles className="w-6 h-6" /></Button>
      </div>
    </div>
  );
};

export default Cases;